import React, { useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Tooltip,
    Alert,
    FormHelperText,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    CardActions,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Usb as UsbIcon,
    Wifi as WifiIcon,
    Print as PrintIcon,
    PrintDisabled as PrintDisabledIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Replay as ReplayIcon,
    HourglassEmpty as PendingIcon,
    Preview as PreviewIcon,
    Stop as StopIcon,
    Warning as WarningIcon,
    Add as AddIcon,
    Download as DownloadIcon,
    Router as RouterIcon,
} from '@mui/icons-material';
import { PageHeader, EmptyState, ViewToggle } from '../../components/admin';
import type { ViewMode } from '../../components/admin';
import {
    useDevicesQuery,
    useDeviceByIdQuery,
    useUpdateDeviceMutation,
    useDeleteDeviceMutation,
    useDevicePrintLogsQuery,
    useDevicePrintQueueQuery,
    useTestDevicePrintMutation,
    useRetryPrintMutation,
    useStartPreviewAlertMutation,
    useStopPreviewAlertMutation,
    usePreviewAlertStatusQuery,
} from '../../api/JsdhNotifikaceService/service/DeviceMutations';
import type {
    DeviceDetail,
    DevicePrintLog,
    UpdateDeviceSettingsRequest,
    PrintQueueJob,
    IdleDisplayMode,
    OrientaceDispleje,
} from '../../api/JsdhNotifikaceService/generated/models/Device';
import DeviceMetricsTab from '../../components/DeviceMetricsTab';
import PoplachConfigForm from '../../components/PoplachConfigForm';
import { parsePoplachConfig } from '../../api/JsdhNotifikaceService/generated';
import {
    NAPOVEDA_ORIENTACE_MONITORU, ORIENTACE_MONITORU_VOLBY, UPOZORNENI_PRESET_NA_VYSKU, zobrazitUpozorneniNaVysku,
} from '../../utils/orientaceMonitoru';
import type { PoplachConfig } from '../../api/JsdhNotifikaceService/generated';
import AddRpiDialog from '../../components/devices/AddRpiDialog';
import type { RpiDialogDevice } from '../../components/devices/AddRpiDialog';

// Helper to format relative time
const formatRelativeTime = (dateStr: string | null): string => {
    if (!dateStr) return 'Nikdy';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Právě teď';
    if (diffMins < 60) return `Před ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Před ${diffHours} hod`;
    const diffDays = Math.floor(diffHours / 24);
    return `Před ${diffDays} dny`;
};

// Helper to check if device is online (last seen within 10 minutes)
const isDeviceOnline = (lastSeen: string | null): boolean => {
    if (!lastSeen) return false;
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    return diffMs < 10 * 60 * 1000; // 10 minutes
};

// Helper to check if printer is stale (not seen for more than 30 minutes)
const isPrinterStale = (lastSeen: string | null): boolean => {
    if (!lastSeen) return true; // No lastSeen = stale
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    return diffMs > 30 * 60 * 1000; // 30 minutes
};

// Status chip component
const DeviceStatusChip: React.FC<{ lastSeen: string | null }> = ({ lastSeen }) => {
    const online = isDeviceOnline(lastSeen);
    return (
        <Chip
            label={online ? 'Online' : 'Offline'}
            color={online ? 'success' : 'default'}
            size="small"
        />
    );
};

// Device detail dialog component (read-only information)
const DeviceDetailDialog: React.FC<{
    deviceId: number | null;
    open: boolean;
    onClose: () => void;
    onOpenSettings: () => void;
}> = ({ deviceId, open, onClose, onOpenSettings }) => {
    const { data: device, isLoading } = useDeviceByIdQuery(deviceId || 0);
    const updateMutation = useUpdateDeviceMutation();
    const [activeTab, setActiveTab] = useState(0);

    const handleSelectPrinter = (printerUri: string | null) => {
        if (deviceId) {
            updateMutation.mutate({
                id: deviceId,
                data: { selectedPrinterUri: printerUri },
            });
        }
    };

    // Reset tab when dialog opens with new device
    React.useEffect(() => {
        if (deviceId) {
            setActiveTab(0);
        }
    }, [deviceId]);

    if (!deviceId) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Detail zařízení: {device?.name || device?.hostname || `ID ${deviceId}`}
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : device ? (
                    <>
                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                            <Tab label="Detail" />
                            <Tab label="Metriky" />
                            <Tab label="Fronta tisku" />
                            <Tab label="Historie tisku" />
                        </Tabs>

                        {activeTab === 0 && (
                            <Box sx={{ pt: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Název:</strong> {device.name || '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Hostname:</strong> {device.hostname || '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>IP adresa:</strong> {device.ipAddress || '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Stav:</strong>{' '}
                                    <DeviceStatusChip lastSeen={device.lastSeen} />
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Device Token:</strong>{' '}
                                    <code>{device.deviceToken.substring(0, 12)}...</code>
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Poslední aktivita:</strong>{' '}
                                    {device.lastSeen
                                        ? new Date(device.lastSeen).toLocaleString('cs-CZ')
                                        : '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Registrováno:</strong>{' '}
                                    {new Date(device.createdAt).toLocaleString('cs-CZ')}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Tisk povolen:</strong> {device.printEnabled ? 'Ano' : 'Ne'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Úspěšné tisky:</strong> {device.successPrints}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Neúspěšné tisky:</strong> {device.failedPrints}
                                </Typography>

                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Data zařízení se aktualizují každých 5 minut.
                                </Alert>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Nalezené tiskárny ({device.availablePrinters.length})
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Seznam tiskáren se aktualizuje každých 5 minut při hlášení metrik ze zařízení.
                                </Typography>

                                {device.availablePrinters.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Žádná tiskárna nebyla nalezena
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {/* Automatický výběr option */}
                                        <Box
                                            onClick={() => handleSelectPrinter('')}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1,
                                                borderRadius: 1,
                                                bgcolor: !device.selectedPrinterUri ? 'action.selected' : 'action.hover',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: !device.selectedPrinterUri ? 'action.selected' : 'action.focus',
                                                },
                                                opacity: updateMutation.isPending ? 0.5 : 1,
                                            }}
                                        >
                                            <PrintIcon fontSize="small" color="action" />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                    Automatický výběr
                                                    {!device.selectedPrinterUri && (
                                                        <Chip
                                                            label="Aktivní"
                                                            size="small"
                                                            color="primary"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Systém vybere první dostupnou tiskárnu
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {device.availablePrinters.map((printer) => {
                                            const stale = isPrinterStale(printer.lastSeen);
                                            const isSelected = device.selectedPrinterUri === printer.uri;
                                            return (
                                                <Box
                                                    key={printer.uri}
                                                    onClick={() => handleSelectPrinter(printer.uri)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        p: 1,
                                                        borderRadius: 1,
                                                        bgcolor: isSelected ? 'action.selected' : 'action.hover',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: isSelected ? 'action.selected' : 'action.focus',
                                                        },
                                                        opacity: updateMutation.isPending ? 0.5 : 1,
                                                    }}
                                                >
                                                    {printer.connectionType === 'usb' ? (
                                                        <UsbIcon fontSize="small" color="action" />
                                                    ) : (
                                                        <WifiIcon fontSize="small" color="action" />
                                                    )}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2">
                                                            {printer.name || printer.uri}
                                                            {isSelected && (
                                                                <Chip
                                                                    label="Vybraná"
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ ml: 1 }}
                                                                />
                                                            )}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {printer.uri}
                                                        </Typography>
                                                    </Box>
                                                    {stale && (
                                                        <Tooltip title={`Naposledy viděna: ${formatRelativeTime(printer.lastSeen)}`}>
                                                            <WarningIcon fontSize="small" color="warning" />
                                                        </Tooltip>
                                                    )}
                                                    {!stale && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatRelativeTime(printer.lastSeen)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {activeTab === 1 && <DeviceMetricsTab deviceId={deviceId} />}

                        {activeTab === 2 && <PrintQueueTab deviceId={deviceId} />}

                        {activeTab === 3 && <PrintLogsTab deviceId={deviceId} />}
                    </>
                ) : (
                    <Alert severity="error">Zařízení nebylo nalezeno.</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zavřít</Button>
                <Button
                    onClick={() => {
                        onClose();
                        onOpenSettings();
                    }}
                    variant="contained"
                    startIcon={<EditIcon />}
                >
                    Nastavení
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Edit dialog component (settings only)
const EditDeviceDialog: React.FC<{
    deviceId: number | null;
    open: boolean;
    onClose: () => void;
}> = ({ deviceId, open, onClose }) => {
    const { data: device, isLoading } = useDeviceByIdQuery(deviceId || 0);
    const updateMutation = useUpdateDeviceMutation();
    const startPreviewMutation = useStartPreviewAlertMutation();
    const stopPreviewMutation = useStopPreviewAlertMutation();
    const { data: previewStatus } = usePreviewAlertStatusQuery(deviceId || 0, open && !!deviceId);

    const [formData, setFormData] = useState<UpdateDeviceSettingsRequest>({});
    const [poplachConfig, setPoplachConfig] = useState<PoplachConfig>(() => parsePoplachConfig());
    const [activeTab, setActiveTab] = useState(0);
    const [previewDuration, setPreviewDuration] = useState(60);

    const isPreviewActive = previewStatus?.active ?? false;

    // Update form when device data loads or deviceId changes
    React.useEffect(() => {
        if (device) {
            setFormData({
                name: device.name || '',
                // Print settings
                printEnabled: device.printEnabled,
                printCopies: device.printCopies,
                selectedPrinterUri: device.selectedPrinterUri,
                // Monitor settings
                sleepEnabled: device.sleepEnabled,
                sleepTimeoutMinutes: device.sleepTimeoutMinutes,
                idleDisplayMode: device.idleDisplayMode,
                orientaceDispleje: device.orientaceDispleje ?? 'NORMAL',
            });
            setPoplachConfig(parsePoplachConfig(device.poplachConfig));
        } else {
            // Reset form when deviceId changes to null or different device
            setFormData({});
            setPoplachConfig(parsePoplachConfig());
        }
    }, [device, deviceId]);

    const handleSave = () => {
        if (deviceId) {
            updateMutation.mutate(
                { id: deviceId, data: { ...formData, poplachConfig: JSON.stringify(poplachConfig) } },
                { onSuccess: () => onClose() }
            );
        }
    };

    if (!deviceId) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Nastavení zařízení: {device?.name || device?.hostname || `ID ${deviceId}`}
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : device ? (
                    <>
                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                            <Tab label="Nastavení" />
                            <Tab label="Obrazovka" />
                            <Tab label="Fronta tisku" />
                            <Tab label="Historie tisku" />
                        </Tabs>

                        {activeTab === 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                                <TextField
                                    label="Název zařízení"
                                    value={formData.name || ''}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    fullWidth
                                    placeholder={device.hostname || 'Bez názvu'}
                                    disabled={updateMutation.isPending}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.printEnabled || false}
                                            onChange={(e) =>
                                                setFormData({ ...formData, printEnabled: e.target.checked })
                                            }
                                            disabled={updateMutation.isPending}
                                        />
                                    }
                                    label="Povolit automatický tisk"
                                />

                                {formData.printEnabled && (
                                    <>
                                        <TextField
                                            label="Počet kopií"
                                            type="number"
                                            value={formData.printCopies || 1}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                // Validace rozsahu 1-10
                                                const copies = isNaN(value) ? 1 : Math.max(1, Math.min(10, value));
                                                setFormData({ ...formData, printCopies: copies });
                                            }}
                                            inputProps={{ min: 1, max: 10 }}
                                            sx={{ width: 150 }}
                                            disabled={updateMutation.isPending}
                                        />

                                        <FormControl fullWidth disabled={updateMutation.isPending}>
                                            <InputLabel>Tiskárna</InputLabel>
                                            <Select
                                                value={formData.selectedPrinterUri || ''}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        selectedPrinterUri: e.target.value,
                                                    })
                                                }
                                                label="Tiskárna"
                                            >
                                                <MenuItem value="">
                                                    <em>Automatický výběr</em>
                                                </MenuItem>
                                                {device.availablePrinters.map((printer) => {
                                                    const stale = isPrinterStale(printer.lastSeen);
                                                    return (
                                                        <MenuItem key={printer.uri} value={printer.uri}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                {printer.connectionType === 'usb' ? (
                                                                    <UsbIcon fontSize="small" />
                                                                ) : (
                                                                    <WifiIcon fontSize="small" />
                                                                )}
                                                                <span style={{ flex: 1 }}>
                                                                    {printer.name || printer.uri}
                                                                </span>
                                                                {stale && (
                                                                    <Tooltip title={`Tiskárna nebyla viděna: ${formatRelativeTime(printer.lastSeen)}`}>
                                                                        <WarningIcon
                                                                            fontSize="small"
                                                                            color="warning"
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>

                                        {device.availablePrinters.length === 0 && (
                                            <Alert severity="info">
                                                Zatím nebyla nahlášena žádná dostupná tiskárna. Připojte
                                                tiskárnu k zařízení a vyčkejte na další hlášení metrik.
                                            </Alert>
                                        )}
                                    </>
                                )}

                                <Divider sx={{ my: 3 }} />

                                {/* Sekce Monitor */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.sleepEnabled ?? true}
                                            onChange={(e) =>
                                                setFormData({ ...formData, sleepEnabled: e.target.checked })
                                            }
                                            disabled={updateMutation.isPending}
                                        />
                                    }
                                    label="Povolit uspávání monitoru (DPMS)"
                                />

                                <TextField
                                    label="Timeout po poplachu (minuty)"
                                    type="number"
                                    value={formData.sleepTimeoutMinutes ?? 15}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        const timeout = isNaN(value) ? 15 : Math.max(1, Math.min(60, value));
                                        setFormData({ ...formData, sleepTimeoutMinutes: timeout });
                                    }}
                                    inputProps={{ min: 1, max: 60 }}
                                    sx={{ width: 250 }}
                                    disabled={updateMutation.isPending}
                                    helperText="Po ukončení poplachu čekat 1-60 minut"
                                />

                                {formData.sleepEnabled === false && (
                                    <FormControl fullWidth disabled={updateMutation.isPending}>
                                        <InputLabel>Zobrazení při nečinnosti</InputLabel>
                                        <Select
                                            value={formData.idleDisplayMode ?? 'TEXT'}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    idleDisplayMode: e.target.value as IdleDisplayMode,
                                                })
                                            }
                                            label="Zobrazení při nečinnosti"
                                        >
                                            <MenuItem value="TEXT">
                                                Text &quot;Žádný aktivní poplach&quot;
                                            </MenuItem>
                                            <MenuItem value="BLACK">
                                                Černá obrazovka (šetření panelu)
                                            </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            Černá obrazovka šetří životnost TV/LED panelů
                                        </FormHelperText>
                                    </FormControl>
                                )}

                                <Alert severity="info">
                                    {formData.sleepEnabled !== false
                                        ? 'Monitor se po timeout uspí pomocí DPMS signálu. TV které nepodporují DPMS mohou zůstat zapnuté.'
                                        : 'DPMS je zakázáno. Monitor zůstane vždy zapnutý.'}
                                </Alert>

                                <FormControl fullWidth disabled={updateMutation.isPending}>
                                    <InputLabel id="orientace-monitoru-label">Orientace monitoru</InputLabel>
                                    <Select
                                        labelId="orientace-monitoru-label"
                                        value={formData.orientaceDispleje ?? 'NORMAL'}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                orientaceDispleje: e.target.value as OrientaceDispleje,
                                            })
                                        }
                                        label="Orientace monitoru"
                                    >
                                        {ORIENTACE_MONITORU_VOLBY.map(v => (
                                            <MenuItem key={v.hodnota} value={v.hodnota}>{v.popisek}</MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {NAPOVEDA_ORIENTACE_MONITORU.map(t => (
                                            <Box component="span" key={t} sx={{ display: 'block' }}>{t}</Box>
                                        ))}
                                    </FormHelperText>
                                </FormControl>

                                {zobrazitUpozorneniNaVysku(formData.orientaceDispleje, poplachConfig.preset) && (
                                    <Alert severity="warning" data-testid="upozorneni-orientace-preset">
                                        {UPOZORNENI_PRESET_NA_VYSKU}
                                    </Alert>
                                )}

                                <Divider sx={{ my: 3 }} />

                                {/* Sekce Vzorový poplach */}
                                <Typography variant="h6" gutterBottom>
                                    Vzorový poplach
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Spustí ukázkové zobrazení poplachu na monitoru bez ukládání do databáze
                                    a bez odesílání notifikací.
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {isPreviewActive ? (
                                        // Preview je aktivní - zobraz pouze tlačítko Ukončit
                                        <>
                                            <Chip
                                                label="Vzorový poplach běží"
                                                color="warning"
                                                size="small"
                                            />
                                            <Button
                                                variant="contained"
                                                color="error"
                                                startIcon={<StopIcon />}
                                                onClick={() => stopPreviewMutation.mutate(deviceId!)}
                                                disabled={stopPreviewMutation.isPending}
                                            >
                                                {stopPreviewMutation.isPending ? 'Ukončuji...' : 'Ukončit vzorový poplach'}
                                            </Button>
                                        </>
                                    ) : (
                                        // Preview není aktivní - zobraz TextField a tlačítko Spustit
                                        <>
                                            <TextField
                                                label="Doba trvání (sekundy)"
                                                type="number"
                                                value={previewDuration}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value);
                                                    setPreviewDuration(Math.max(10, Math.min(300, value || 60)));
                                                }}
                                                inputProps={{ min: 10, max: 300 }}
                                                sx={{ width: 180 }}
                                                size="small"
                                                disabled={startPreviewMutation.isPending}
                                            />

                                            <Button
                                                variant="contained"
                                                color="warning"
                                                startIcon={<PreviewIcon />}
                                                onClick={() => startPreviewMutation.mutate({
                                                    id: deviceId!,
                                                    durationSeconds: previewDuration,
                                                })}
                                                disabled={startPreviewMutation.isPending || !isDeviceOnline(device?.lastSeen || null)}
                                            >
                                                {startPreviewMutation.isPending ? 'Spouštím...' : 'Spustit vzorový poplach'}
                                            </Button>
                                        </>
                                    )}
                                </Box>

                                {!isPreviewActive && !isDeviceOnline(device?.lastSeen || null) && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        Zařízení je offline. Vzorový poplach lze spustit pouze na online zařízení.
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <Box sx={{ pt: 1 }}>
                                <PoplachConfigForm
                                    config={poplachConfig}
                                    onChange={setPoplachConfig}
                                />
                            </Box>
                        )}

                        {activeTab === 2 && <PrintQueueTab deviceId={deviceId} />}

                        {activeTab === 3 && <PrintLogsTab deviceId={deviceId} />}
                    </>
                ) : (
                    <Alert severity="error">Zařízení nebylo nalezeno.</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={updateMutation.isPending}
                >
                    {updateMutation.isPending ? 'Ukládání...' : 'Uložit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Print logs tab component
const PrintLogsTab: React.FC<{ deviceId: number }> = ({ deviceId }) => {
    const { data: logs, isLoading } = useDevicePrintLogsQuery(deviceId);

    const columns: GridColDef<DevicePrintLog>[] = [
        {
            field: 'printedAt',
            headerName: 'Čas tisku',
            width: 180,
            valueGetter: (_, row) =>
                new Date(row.printedAt).toLocaleString('cs-CZ'),
        },
        {
            field: 'poplachId',
            headerName: 'Poplach ID',
            width: 100,
        },
        {
            field: 'success',
            headerName: 'Stav',
            width: 100,
            renderCell: (params) =>
                params.value ? (
                    <SuccessIcon color="success" />
                ) : (
                    <ErrorIcon color="error" />
                ),
        },
        {
            field: 'printerName',
            headerName: 'Tiskárna',
            width: 200,
            valueGetter: (_, row) => row.printerName || '-',
        },
        {
            field: 'errorMessage',
            headerName: 'Chyba',
            flex: 1,
            valueGetter: (_, row) => row.errorMessage || '-',
        },
    ];

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <DataGrid
            rows={logs || []}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: { sortModel: [{ field: 'printedAt', sort: 'desc' }] },
            }}
            autoHeight
            disableRowSelectionOnClick
        />
    );
};

// Print queue tab component
const PrintQueueTab: React.FC<{ deviceId: number }> = ({ deviceId }) => {
    const { data: queue, isLoading } = useDevicePrintQueueQuery(deviceId);
    const retryMutation = useRetryPrintMutation();

    const getStatusChip = (status: PrintQueueJob['status']) => {
        switch (status) {
            case 'PENDING':
                return <Chip icon={<PendingIcon />} label="Čeká" color="warning" size="small" />;
            case 'COMPLETED':
                return <Chip icon={<SuccessIcon />} label="Hotovo" color="success" size="small" />;
            case 'FAILED':
                return <Chip icon={<ErrorIcon />} label="Chyba" color="error" size="small" />;
        }
    };

    const getJobTypeLabel = (jobType: PrintQueueJob['jobType']) => {
        return jobType === 'ALERT' ? 'Poplach' : 'Test';
    };

    const columns: GridColDef<PrintQueueJob>[] = [
        {
            field: 'createdAt',
            headerName: 'Vytvořeno',
            width: 160,
            valueGetter: (_, row) =>
                new Date(row.createdAt).toLocaleString('cs-CZ'),
        },
        {
            field: 'jobType',
            headerName: 'Typ',
            width: 90,
            valueGetter: (_, row) => getJobTypeLabel(row.jobType),
        },
        {
            field: 'poplachId',
            headerName: 'Poplach',
            width: 80,
            valueGetter: (_, row) => row.poplachId || '-',
        },
        {
            field: 'status',
            headerName: 'Stav',
            width: 110,
            renderCell: (params) => getStatusChip(params.row.status),
        },
        {
            field: 'retryCount',
            headerName: 'Pokus',
            width: 80,
            valueGetter: (_, row) => `${row.retryCount + 1}/${row.maxRetries}`,
        },
        {
            field: 'lastError',
            headerName: 'Chyba',
            flex: 1,
            valueGetter: (_, row) => row.lastError || '-',
        },
        {
            field: 'actions',
            headerName: 'Akce',
            width: 80,
            sortable: false,
            renderCell: (params) => (
                params.row.status === 'FAILED' ? (
                    <Tooltip title="Opakovat tisk">
                        <IconButton
                            onClick={() => retryMutation.mutate({
                                deviceId,
                                jobId: params.row.id,
                            })}
                            size="small"
                            disabled={retryMutation.isPending}
                        >
                            <ReplayIcon />
                        </IconButton>
                    </Tooltip>
                ) : null
            ),
        },
    ];

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    const pendingOrFailedJobs = queue?.filter(
        (job) => job.status === 'PENDING' || job.status === 'FAILED'
    ) || [];

    return (
        <Box>
            {pendingOrFailedJobs.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                    Ve frontě nejsou žádné čekající ani neúspěšné úlohy.
                </Alert>
            ) : (
                <DataGrid
                    rows={pendingOrFailedJobs}
                    columns={columns}
                    pageSizeOptions={[10, 25]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                        sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
                    }}
                    autoHeight
                    disableRowSelectionOnClick
                />
            )}
        </Box>
    );
};

// Delete confirmation dialog
const DeleteDeviceDialog: React.FC<{
    device: DeviceDetail | null;
    open: boolean;
    onClose: () => void;
}> = ({ device, open, onClose }) => {
    const deleteMutation = useDeleteDeviceMutation();

    const handleDelete = () => {
        if (device) {
            deleteMutation.mutate(device.id, { onSuccess: () => onClose() });
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Smazat zařízení?</DialogTitle>
            <DialogContent>
                <Typography>
                    Opravdu chcete smazat zařízení{' '}
                    <strong>{device?.name || device?.hostname || `ID ${device?.id}`}</strong>?
                    Tato akce je nevratná.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                <Button
                    onClick={handleDelete}
                    color="error"
                    variant="contained"
                    disabled={deleteMutation.isPending}
                >
                    {deleteMutation.isPending ? 'Mazání...' : 'Smazat'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Action buttons for a device (shared between table and card views)
const DeviceActions: React.FC<{
    device: DeviceDetail;
    demoEnabled: boolean;
    testPrintMutation: ReturnType<typeof useTestDevicePrintMutation>;
    onDownload: (device: RpiDialogDevice) => void;
    onEdit: (id: number) => void;
    onDelete: (device: DeviceDetail) => void;
}> = ({ device, demoEnabled, testPrintMutation, onDownload, onEdit, onDelete }) => (
    <Box onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Testovací tisk">
            <span>
                <IconButton
                    onClick={() => testPrintMutation.mutate(device.id)}
                    size="small"
                    disabled={!device.printEnabled || testPrintMutation.isPending}
                    color="primary"
                >
                    <PrintIcon />
                </IconButton>
            </span>
        </Tooltip>
        {!demoEnabled && (
            <Tooltip title="Stáhnout ZIP">
                <IconButton
                    onClick={() => onDownload({
                        id: device.id,
                        hostname: device.hostname,
                        name: device.name,
                    })}
                    size="small"
                    color="info"
                >
                    <DownloadIcon />
                </IconButton>
            </Tooltip>
        )}
        <Tooltip title="Upravit">
            <IconButton onClick={() => onEdit(device.id)} size="small">
                <EditIcon />
            </IconButton>
        </Tooltip>
        <Tooltip title="Smazat">
            <IconButton
                onClick={() => onDelete(device)}
                size="small"
                color="error"
            >
                <DeleteIcon />
            </IconButton>
        </Tooltip>
    </Box>
);

// Table view for devices
const DevicesTableView: React.FC<{
    devices: DeviceDetail[];
    demoEnabled: boolean;
    testPrintMutation: ReturnType<typeof useTestDevicePrintMutation>;
    onRowClick: (id: number) => void;
    onDownload: (device: RpiDialogDevice) => void;
    onEdit: (id: number) => void;
    onDelete: (device: DeviceDetail) => void;
}> = ({ devices, demoEnabled, testPrintMutation, onRowClick, onDownload, onEdit, onDelete }) => (
    <TableContainer>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Název</TableCell>
                    <TableCell>Hostname</TableCell>
                    <TableCell>IP adresa</TableCell>
                    <TableCell>Poslední aktivita</TableCell>
                    <TableCell>Stav</TableCell>
                    <TableCell align="center">Tisk</TableCell>
                    <TableCell>Tisky</TableCell>
                    <TableCell>Akce</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {devices.map((device) => (
                    <TableRow
                        key={device.id}
                        hover
                        onClick={() => onRowClick(device.id)}
                        sx={{ cursor: 'pointer' }}
                    >
                        <TableCell>{device.name || device.hostname || '-'}</TableCell>
                        <TableCell>{device.hostname || '-'}</TableCell>
                        <TableCell>{device.ipAddress || '-'}</TableCell>
                        <TableCell>{formatRelativeTime(device.lastSeen)}</TableCell>
                        <TableCell><DeviceStatusChip lastSeen={device.lastSeen} /></TableCell>
                        <TableCell align="center">
                            {device.printEnabled ? (
                                <Tooltip title="Tisk povolen"><PrintIcon color="success" /></Tooltip>
                            ) : (
                                <Tooltip title="Tisk zakázán"><PrintDisabledIcon color="disabled" /></Tooltip>
                            )}
                        </TableCell>
                        <TableCell>{device.successPrints}/{device.successPrints + device.failedPrints}</TableCell>
                        <TableCell>
                            <DeviceActions
                                device={device}
                                demoEnabled={demoEnabled}
                                testPrintMutation={testPrintMutation}
                                onDownload={onDownload}
                                onEdit={onEdit}
                                onDelete={onDelete}
                            />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

// Card view for devices
const DevicesCardView: React.FC<{
    devices: DeviceDetail[];
    demoEnabled: boolean;
    testPrintMutation: ReturnType<typeof useTestDevicePrintMutation>;
    onCardClick: (id: number) => void;
    onDownload: (device: RpiDialogDevice) => void;
    onEdit: (id: number) => void;
    onDelete: (device: DeviceDetail) => void;
}> = ({ devices, demoEnabled, testPrintMutation, onCardClick, onDownload, onEdit, onDelete }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        {devices.map((device) => (
            <Card
                key={device.id}
                variant="outlined"
                sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                onClick={() => onCardClick(device.id)}
            >
                <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                            {device.name || device.hostname || '-'}
                        </Typography>
                        <DeviceStatusChip lastSeen={device.lastSeen} />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {device.hostname && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Hostname:</strong> {device.hostname}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            <strong>IP:</strong> {device.ipAddress || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Poslední aktivita:</strong> {formatRelativeTime(device.lastSeen)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Tisk:</strong>
                            </Typography>
                            {device.printEnabled ? (
                                <Chip label="Povolen" color="success" size="small" variant="outlined" />
                            ) : (
                                <Chip label="Zakázán" size="small" variant="outlined" />
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                {device.successPrints}/{device.successPrints + device.failedPrints}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
                <CardActions sx={{ pt: 0 }}>
                    <DeviceActions
                        device={device}
                        demoEnabled={demoEnabled}
                        testPrintMutation={testPrintMutation}
                        onDownload={onDownload}
                        onEdit={onEdit}
                        onDelete={onDelete}
                    />
                </CardActions>
            </Card>
        ))}
    </Box>
);

// Main page component
const DevicesPage: React.FC = () => {
    const { data: devices, isLoading, refetch, isRefetching } = useDevicesQuery();
    const { data: demoStatus } = useQuery({
        queryKey: ['demoStatus'],
        queryFn: () => axiosInstance.get('/api/demo/status').then(r => r.data),
    });
    const demoEnabled = demoStatus?.demoEnabled === true;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? 'cards' : 'table');
    const [editDeviceId, setEditDeviceId] = useState<number | null>(null);
    const [detailDeviceId, setDetailDeviceId] = useState<number | null>(null);
    const [deleteDevice, setDeleteDevice] = useState<DeviceDetail | null>(null);
    const [addRpiOpen, setAddRpiOpen] = useState(false);
    const [downloadDevice, setDownloadDevice] = useState<RpiDialogDevice | null>(null);
    const testPrintMutation = useTestDevicePrintMutation();

    const actionProps = {
        demoEnabled,
        testPrintMutation,
        onDownload: setDownloadDevice,
        onEdit: setEditDeviceId,
        onDelete: setDeleteDevice,
    };

    return (
        <Box>
            <PageHeader
                title="Správa zařízení"
                action={
                    <Box display="flex" gap={1} alignItems="center">
                        <ViewToggle value={viewMode} onChange={setViewMode} />
                        {!demoEnabled && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setAddRpiOpen(true)}
                            >
                                Přidat RPi
                            </Button>
                        )}
                        <Tooltip title="Obnovit">
                            <IconButton onClick={() => refetch()} disabled={isRefetching}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                }
            />

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : devices && devices.length > 0 ? (
                viewMode === 'cards' ? (
                    <DevicesCardView
                        devices={devices}
                        {...actionProps}
                        onCardClick={setDetailDeviceId}
                    />
                ) : (
                    <Paper sx={{ p: 2 }}>
                        <DevicesTableView
                            devices={devices}
                            {...actionProps}
                            onRowClick={setDetailDeviceId}
                        />
                    </Paper>
                )
            ) : (
                <EmptyState
                    icon={<RouterIcon />}
                    title="Žádná zařízení"
                    description="Raspberry Pi se zaregistruje automaticky po připojení k síti."
                />
            )}

            <DeviceDetailDialog
                deviceId={detailDeviceId}
                open={detailDeviceId !== null}
                onClose={() => setDetailDeviceId(null)}
                onOpenSettings={() => setEditDeviceId(detailDeviceId)}
            />

            <EditDeviceDialog
                deviceId={editDeviceId}
                open={editDeviceId !== null}
                onClose={() => setEditDeviceId(null)}
            />

            <DeleteDeviceDialog
                device={deleteDevice}
                open={deleteDevice !== null}
                onClose={() => setDeleteDevice(null)}
            />

            <AddRpiDialog
                open={addRpiOpen}
                onClose={() => setAddRpiOpen(false)}
            />

            <AddRpiDialog
                open={downloadDevice !== null}
                onClose={() => setDownloadDevice(null)}
                device={downloadDevice}
            />
        </Box>
    );
};

export default DevicesPage;
